import { useState, useEffect, useRef } from "react";
import { X } from "@phosphor-icons/react";
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";

interface OtpVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (otpCode: string) => Promise<{ success: boolean; error?: string }>;
  onResend?: () => Promise<{ success: boolean; error?: string }>;
  userEmail?: string;
  sessionId: string;
  otpMethod?: 'totp' | 'email'; // 'totp' for authenticator app, 'email' for email OTP
}

export default function OtpVerificationModal({
  isOpen,
  onClose,
  onVerify,
  onResend,
  userEmail,
  sessionId,
  otpMethod = 'totp',
}: OtpVerificationModalProps) {
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEmailOTP = otpMethod === 'email';

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setOtpCode("");
      setError("");
      setResendSuccess(false);
    }
  }, [isOpen]);

  const handleResend = async () => {
    if (!onResend) return;

    setIsResending(true);
    setError("");
    setResendSuccess(false);

    try {
      const result = await onResend();
      if (result.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 3000);
      } else {
        setError(result.error || "Failed to resend OTP. Please try again.");
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode || otpCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const result = await onVerify(otpCode);

      if (!result.success) {
        setError(result.error || "Invalid OTP code. Please try again.");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError("Verification failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(value);
    setError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Two-Factor Authentication
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            {isEmailOTP
              ? `Enter the 6-digit verification code sent to your email${userEmail ? ` (${userEmail})` : ''}.`
              : "Enter the 6-digit verification code from your authenticator app."}
          </p>
          {userEmail && !isEmailOTP && (
            <p className="text-xs text-gray-500">
              Logging in as: <span className="font-medium">{userEmail}</span>
            </p>
          )}
          {isEmailOTP && (
            <p className="text-xs text-gray-500 mt-2">
              The code will expire in 5 minutes. Didn't receive it?{" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-[#4F46E5] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? "Resending..." : "Resend code"}
              </button>
            </p>
          )}
          {resendSuccess && (
            <p className="text-xs text-green-600 mt-2">
              ✓ New code sent! Please check your email.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <Input
              ref={inputRef}
              type="text"
              value={otpCode}
              onChange={handleOtpChange}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
              disabled={isSubmitting}
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || otpCode.length !== 6}
              className="flex-1"
            >
              {isSubmitting ? "Verifying..." : "Verify"}
            </Button>
          </div>
        </form>

        {!isEmailOTP && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Can't access your authenticator app? Contact your administrator.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
